import { resolveColour, type ColourRoleName } from "odyssey-shared";
import { LOW_HEALTH_STEADY_ROLE, healthFill, healthLow, lowHealthLit } from "../../../ui/kit/health.ts";
import { buffsMaxHealthPercent } from "../../hud/health-penalty.ts";
import { PAGE_RULES } from "../../rules.ts";
import type { NameplateChoices } from "../../settings.ts";
import { ABOVE_HEAD } from "../nameplates/layout.ts";
import { plateForm } from "../nameplates/look.ts";

/**
 * The health bar under a character's nameplate (P2.1-T4, question 5; the design system's nameplate bar, 32 by 4 with
 * a one-pixel edge): one sprite whose frame is cut from one strip holding the bar at every fill, so a change of
 * health changes the frame and nothing is drawn or measured again (ADR-061 decision 3, "a bar is a sprite that
 * crops"). The plate's name stands over it; the bar stands where the name would with no bar, and the name rises.
 * Below 10 percent its fill flashes, or under reduced motion stays lit in the steady colour, by the kit's health rule
 * (`ui/kit/health.ts`), from a second run of frames in the same strip. Under a maximum-health penalty its Buffs list
 * names (ADR-079's amendment of 2026-10-03 for P2.2-T16) the fill is drawn against the full maximum and the part the
 * penalty removes is locked at the bar's end, an image of its own over the bar (`locked.ts`).
 */

/**
 * The bar's size in logical pixels, and the inside its fill covers.
 */
export const HEALTH_BAR = Object.freeze({ width: 32, height: 4, inside: 30 });

/**
 * How far a plate's name rises over a bar: the bar's height and a one-pixel gap.
 */
export const PLATE_LIFT = HEALTH_BAR.height + 1;

/**
 * The texture key the strip is drawn into.
 */
export const HEALTH_BAR_TEXTURE = "health-bar";

/**
 * How much of the highlight row is white over the health colour, as the design's inset highlight.
 */
const HIGHLIGHT = 0.35;

const FRAMES: readonly string[] = Object.freeze(Array.from({ length: HEALTH_BAR.inside + 1 }, (_, fill) => `${HEALTH_BAR_TEXTURE}/${fill}`));
const STEADY_FRAMES: readonly string[] = Object.freeze(Array.from({ length: HEALTH_BAR.inside + 1 }, (_, fill) => `${HEALTH_BAR_TEXTURE}/steady/${fill}`));

/**
 * How many frames the strip holds: one bar for each fill in the health colour, then one for each fill in the steady
 * colour that a low bar takes under reduced motion.
 */
export const HEALTH_BAR_FRAMES = FRAMES.length + STEADY_FRAMES.length;

/**
 * The maximum-health modifier a character's Buffs list carries, each effect at one stack, from the page's rules
 * (`buffsMaxHealthPercent`): negative under a penalty, which the bar draws locked. Allocates nothing.
 *
 * @param buffs The Buffs component's effect numbers.
 * @returns Whole percents, 0 for none.
 */
export function barPenaltyOf(buffs: readonly number[]): number {
  return buffs.length === 0 ? 0 : buffsMaxHealthPercent(PAGE_RULES, buffs);
}

/**
 * Whether a character's bar shows: its plate is drawn in some form by the viewer's Nameplates choices, since the bar
 * lies under the plate and follows it (the player's own plate is off by default, so is its bar), and it is below full
 * health and not dead, whose body says it (question 5's default names every character not at full health; the dead
 * lie down instead). Under a maximum-health penalty full health is the unpenalised maximum, so a penalised character
 * at its reduced maximum shows its bar. The plates and the chat bubbles rise over a bar by the same predicate.
 *
 * @param health The Health component's percentage, of the maximum the server holds.
 * @param own Whether the character is the viewer's own.
 * @param choices The viewer's Nameplates choices.
 * @param penalty Its Buffs list's maximum-health modifier (`barPenaltyOf`), 0 for none.
 * @returns True when the bar shows.
 */
export function barShown(health: number, own: boolean, choices: Readonly<NameplateChoices>, penalty = 0): boolean {
  return health > 0 && (health < 100 || penalty < 0) && plateForm(own, choices) !== "none";
}

/**
 * The share of the full maximum a penalty leaves, in whole percents: 100 for none or a buff, never below 1.
 *
 * @param penalty The maximum-health modifier.
 * @returns The share.
 */
function shareLeft(penalty: number): number {
  return penalty >= 0 ? 100 : Math.max(1, 100 + penalty);
}

/**
 * The width of the locked segment at a bar's end under a maximum-health penalty: the inside the reduced maximum does
 * not reach, at least one pixel for any penalty, so Resurrection Sickness's 2 percent shows on 30 pixels, and at most
 * all but one, so a living character's fill keeps its pixel.
 *
 * @param penalty The maximum-health modifier (`barPenaltyOf`).
 * @returns Logical pixels, 0 for none.
 * @example
 * barLockedOf(-2);
 * // 1
 */
export function barLockedOf(penalty: number): number {
  if (penalty >= 0) {
    return 0;
  }
  return Math.min(HEALTH_BAR.inside - 1, Math.max(1, HEALTH_BAR.inside - Math.floor((HEALTH_BAR.inside * shareLeft(penalty)) / 100)));
}

/**
 * The fill for a Health percentage under a maximum-health penalty: the percentage is of the reduced maximum, so the
 * fill is its share of the reduced maximum's share of the inside, at least one pixel for any health, and never into
 * the locked segment; with no penalty it is `barFillOf`.
 *
 * @param health The percentage.
 * @param penalty The maximum-health modifier (`barPenaltyOf`).
 * @returns The fill in logical pixels.
 * @example
 * barFillUnder(100, -2);
 * // 29
 */
export function barFillUnder(health: number, penalty: number): number {
  if (penalty >= 0) {
    return barFillOf(health);
  }
  return Math.min(HEALTH_BAR.inside - barLockedOf(penalty), healthFill(health * shareLeft(penalty), 10_000, HEALTH_BAR.inside));
}

/**
 * The fill for a Health percentage: the inside's share rounded down to a whole pixel, full only at 100, and at least
 * one pixel for any health above 0 (`healthFill`).
 *
 * @param health The percentage.
 * @returns The fill in logical pixels, 0 to the inside.
 */
export function barFillOf(health: number): number {
  return healthFill(health, 100, HEALTH_BAR.inside);
}

/**
 * The strip's frame for a fill in the health colour; the names are made once.
 *
 * @param fill The fill in logical pixels.
 * @returns The frame's name.
 */
export function barFrame(fill: number): string {
  return FRAMES[fill] as string;
}

/**
 * The frame a bar shows for a Health percentage at a moment of the scene's clock. At 10 percent and above it is the
 * fill's frame. Below 10 percent the fill flashes: its frame in the lit phase of `lowHealthLit` and the empty track in
 * its edge in the dark phase, so the bar itself never vanishes and every bar read on the same frame is in step; under
 * reduced motion the fill stays, in the steady colour. The names are made once, so nothing is allocated.
 *
 * @param health The percentage.
 * @param timeMs The scene's clock in ms.
 * @param reducedMotion Whether the viewer asks for reduced motion.
 * @param penalty The maximum-health modifier (`barPenaltyOf`), 0 for none: the fill is drawn against the full maximum.
 * @returns The frame's name.
 */
export function barFrameOf(health: number, timeMs: number, reducedMotion: boolean, penalty = 0): string {
  const fill = barFillUnder(health, penalty);
  if (!healthLow(health, 100)) {
    return FRAMES[fill] as string;
  }
  if (reducedMotion) {
    return STEADY_FRAMES[fill] as string;
  }
  return (lowHealthLit(timeMs) ? FRAMES[fill] : FRAMES[0]) as string;
}

/**
 * Every frame's name and its row in the strip, in the strip's order, for cutting the frames from the texture.
 *
 * @param each Called with each frame's name and the index of its bar in the strip from the top.
 */
export function forEachBarFrame(each: (name: string, index: number) => void): void {
  FRAMES.forEach((name, index) => each(name, index));
  STEADY_FRAMES.forEach((name, index) => each(name, FRAMES.length + index));
}

/**
 * A colour role as its three channels.
 *
 * @param role The role.
 * @returns Red, green and blue.
 */
function channels(role: ColourRoleName): readonly [number, number, number] {
  const hex = resolveColour(role);
  return [Number.parseInt(hex.slice(1, 3), 16), Number.parseInt(hex.slice(3, 5), 16), Number.parseInt(hex.slice(5, 7), 16)];
}

/**
 * One channel of the highlight row: the health colour's channel moved `HIGHLIGHT` of the way to white.
 *
 * @param value The channel, 0 to 255.
 * @returns The lightened channel.
 */
function lighten(value: number): number {
  return Math.round(value + (255 - value) * HIGHLIGHT);
}

/**
 * Paints one run of bars into the strip, one per fill from 0 to the inside, stacked from a first bar.
 *
 * @param pixels The strip's pixels.
 * @param first The index of the run's first bar in the strip.
 * @param role The fill's colour role.
 */
function paintRun(pixels: Uint8ClampedArray, first: number, role: ColourRoleName): void {
  const { width, height, inside } = HEALTH_BAR;
  const edge = channels("frame-edge");
  const track = channels("bar-track");
  const fillColour = channels(role);
  const highlight = [lighten(fillColour[0]), lighten(fillColour[1]), lighten(fillColour[2])] as const;
  for (let fill = 0; fill <= inside; fill += 1) {
    for (let row = 0; row < height; row += 1) {
      for (let x = 0; x < width; x += 1) {
        const ring = row === 0 || row === height - 1 || x === 0 || x === width - 1;
        const colour = ring ? edge : x - 1 < fill ? (row === 1 ? highlight : fillColour) : track;
        const at = (((first + fill) * height + row) * width + x) * 4;
        pixels[at] = colour[0];
        pixels[at + 1] = colour[1];
        pixels[at + 2] = colour[2];
        pixels[at + 3] = 255;
      }
    }
  }
}

/**
 * The strip every bar's frames are cut from: one bar per fill from 0 to the inside in the health colour, stacked
 * from the top, then the same run with the fill in the steady colour (`LOW_HEALTH_STEADY_ROLE`); each bar an edge
 * ring in the frame edge colour around a two-row inside, the fill's top row a highlight over its colour and its
 * bottom row its colour, the rest the bar track.
 *
 * @returns The strip's size and its RGBA pixels.
 */
export function healthBarStrip(): { readonly width: number; readonly height: number; readonly pixels: Uint8ClampedArray } {
  const { width, height, inside } = HEALTH_BAR;
  const rows = height * HEALTH_BAR_FRAMES;
  const pixels = new Uint8ClampedArray(width * rows * 4);
  paintRun(pixels, 0, "health");
  paintRun(pixels, inside + 1, LOW_HEALTH_STEADY_ROLE);
  return { width, height: rows, pixels };
}

/**
 * Where a bar stands, in logical pixels of the UI layer: centred on the character's feet across and its bottom
 * `ABOVE_HEAD` over the head, laid out from the head's device pixel as a plate is (CLIENT.md 7.16).
 *
 * @param anchorX The head's x in whole device pixels.
 * @param anchorY The head's y in whole device pixels.
 * @param scale The UI scale.
 * @param out Where the bar's top left is written.
 * @returns `out`.
 */
export function barLayout(anchorX: number, anchorY: number, scale: number, out: { x: number; y: number }): { x: number; y: number } {
  out.x = (anchorX - Math.floor((HEALTH_BAR.width * scale) / 2)) / scale;
  out.y = (anchorY - (ABOVE_HEAD + HEALTH_BAR.height) * scale) / scale;
  return out;
}
